import React__default from 'react';
import { Box, Typography, Chip, Link, Tooltip, IconButton } from '@mui/material';
import copy from 'copy-to-clipboard';
import dayjs from 'dayjs';
import DefaultIcon from '../../../DefaultIcon/index.js';
import { isTechAdmin, isAdmin } from '../../../providers/users/utils.js';

function renderTitleCell(row) {
    return React__default.createElement(Box, { title: row.title }, row.title);
}
function renderCreationCell(row, t) {
    var _a;
    var createDate = row.createDate;
    var createUser = (_a = row._embedded) === null || _a === void 0 ? void 0 : _a.createUser[0];
    return (React__default.createElement(Box, { title: "".concat(t("components.kf-general-public-sharing.table.body.cell.creation.date", {
            date: dayjs(createDate).format("L"),
            time: dayjs(createDate).format("LT")
        }), "\n").concat(t("components.kf-general-public-sharing.table.body.cell.creation.user", {
            firstName: createUser === null || createUser === void 0 ? void 0 : createUser.firstName,
            lastName: createUser === null || createUser === void 0 ? void 0 : createUser.lastName
        })) },
        t("components.kf-general-public-sharing.table.body.cell.creation.date", {
            date: dayjs(createDate).format("L")
        }),
        React__default.createElement(Box, { color: "text.disabled" },
            React__default.createElement(Typography, { variant: "caption" }, t("components.kf-general-public-sharing.table.body.cell.creation.user", {
                firstName: createUser === null || createUser === void 0 ? void 0 : createUser.firstName,
                lastName: createUser === null || createUser === void 0 ? void 0 : createUser.lastName
            })))));
}
function renderStateCell(row, theme, t) {
    var active = row.active;
    var activationDate = row.activationDate;
    var deactivationDate = row.deactivationDate;
    var isDeactivationDateOutdated = deactivationDate && dayjs(deactivationDate).isBefore(dayjs());
    return (React__default.createElement(Box, null,
        active && (React__default.createElement(React__default.Fragment, null,
            React__default.createElement(Chip, { label: t("components.kf-general-public-sharing.table.body.cell.state.active"), variant: "outlined", size: "small", style: {
                    borderColor: theme.palette.success.main,
                    color: theme.palette.success.main
                } }),
            activationDate && (React__default.createElement(Box, { color: "text.disabled", title: t("components.kf-general-public-sharing.table.body.cell.state.activatedSince", {
                    activationDate: dayjs(activationDate).format("L"),
                    activationTime: dayjs(activationDate).format("LT")
                }) },
                React__default.createElement(Typography, { variant: "caption" }, t("components.kf-general-public-sharing.table.body.cell.state.activatedSince", {
                    activationDate: dayjs(activationDate).format("L"),
                    activationTime: dayjs(activationDate).format("LT")
                })))),
            deactivationDate && (React__default.createElement(Box, { color: "text.disabled", title: t("components.kf-general-public-sharing.table.body.cell.state.until", {
                    date: dayjs(deactivationDate).format("L"),
                    time: dayjs(deactivationDate).format("LT")
                }) },
                React__default.createElement(Typography, { variant: "caption" }, t("components.kf-general-public-sharing.table.body.cell.state.until", {
                    date: dayjs(deactivationDate).format("L"),
                    time: dayjs(deactivationDate).format("LT")
                })))))),
        !active && (React__default.createElement(React__default.Fragment, null,
            React__default.createElement(Chip, { label: t("components.kf-general-public-sharing.table.body.cell.state.inactive"), variant: "outlined", size: "small", style: {
                    borderColor: theme.palette.error.main,
                    color: theme.palette.error.main
                } }),
            React__default.createElement(Box, { color: "text.disabled" },
                React__default.createElement(Typography, { variant: "caption" },
                    !isDeactivationDateOutdated && activationDate && (React__default.createElement(Box, { component: "span", title: t("components.kf-general-public-sharing.table.body.cell.state.will-be-activated", {
                            date: dayjs(activationDate).format("L"),
                            time: dayjs(activationDate).format("LT")
                        }) }, t("components.kf-general-public-sharing.table.body.cell.state.will-be-activated", {
                        date: dayjs(activationDate).format("L"),
                        time: dayjs(activationDate).format("LT")
                    }))),
                    isDeactivationDateOutdated && (React__default.createElement(Box, { component: "span", title: t("components.kf-general-public-sharing.table.body.cell.state.disabled-since", {
                            date: dayjs(deactivationDate).format("L"),
                            time: dayjs(deactivationDate).format("LT")
                        }) }, t("components.kf-general-public-sharing.table.body.cell.state.disabled-since", {
                        date: dayjs(deactivationDate).format("L"),
                        time: dayjs(deactivationDate).format("LT")
                    })))))))));
}
function renderShareUrlCell(row, enqueueSnackbar, t, KIcon) {
    if (KIcon === void 0) { KIcon = DefaultIcon; }
    var shareUrl = row.shareUrl || "";
    return (React__default.createElement(Box, { style: {
            display: "flex",
            alignItems: "center"
        } },
        React__default.createElement(Box, { title: shareUrl },
            React__default.createElement(Link, { href: "".concat(shareUrl), target: "_blank", underline: "hover" }, "".concat(shareUrl))),
        React__default.createElement(Tooltip, { title: t("components.kf-general-public-sharing.table.body.cell.share-url.clipboard.title") || "" },
            React__default.createElement(IconButton, { "aria-label": "copy", onClick: function () {
                    if (copy(shareUrl)) {
                        enqueueSnackbar(t("components.kf-general-public-sharing.table.body.cell.share-url.clipboard.success"), { variant: "info" });
                    }
                    else {
                        enqueueSnackbar(t("components.kf-general-public-sharing.table.body.cell.share-url.clipboard.error"), {
                            variant: "error"
                        });
                    }
                }, size: "large" },
                React__default.createElement(KIcon, { className: "fa-regular fa-copy" })))));
}
function renderTemplateCell(row, dataLocale) {
    var _a, _b;
    if (!dataLocale) {
        return null;
    }
    if (((_a = row._embedded) === null || _a === void 0 ? void 0 : _a.template) && row._embedded.template.length > 0) {
        var templateI18n = row._embedded.template[0];
        var titleI18n = (_b = templateI18n === null || templateI18n === void 0 ? void 0 : templateI18n.title) === null || _b === void 0 ? void 0 : _b.find(function (t) { return t.lang.toLowerCase() === dataLocale.toLowerCase(); });
        if (titleI18n) {
            return React__default.createElement(Box, { title: titleI18n.value }, titleI18n.value);
        }
    }
    return null;
}
function renderActionsCell(row, t, onDeleteShareToken, onEditShareToken, currentUser, KIcon) {
    if (KIcon === void 0) { KIcon = DefaultIcon; }
    if (isTechAdmin(currentUser) || isAdmin(currentUser) || row.createUser === (currentUser === null || currentUser === void 0 ? void 0 : currentUser.id)) {
        return (React__default.createElement(Box, { textAlign: "right" },
            React__default.createElement(Tooltip, { title: t("components.kf-general-public-sharing.table.body.cell.actions.edit.title") || "" },
                React__default.createElement(IconButton, { "aria-label": "edit", onClick: function () {
                        onEditShareToken(row);
                    }, size: "large" },
                    React__default.createElement(KIcon, { className: "fa-solid fa-pen" }))),
            React__default.createElement(Tooltip, { title: t("components.kf-general-public-sharing.table.body.cell.actions.delete.title") || "" },
                React__default.createElement(IconButton, { "aria-label": "delete", onClick: function () {
                        onDeleteShareToken(row);
                    }, size: "large" },
                    React__default.createElement(KIcon, { className: "fa-solid fa-trash" })))));
    }
    return null;
}

export { renderActionsCell, renderCreationCell, renderShareUrlCell, renderStateCell, renderTemplateCell, renderTitleCell };
//# sourceMappingURL=utils.js.map
